// XadomTableTypes.tsx
import { EntityState } from "@reduxjs/toolkit";
import React from 'react';

// 기본 데이터 타입 정의
export enum ValueType {
    // eslint-disable-next-line no-unused-vars
    GROUP = 0,
    // eslint-disable-next-line no-unused-vars
    CHECKBOX = 1,
    // eslint-disable-next-line no-unused-vars
    STRING = 2,
    // eslint-disable-next-line no-unused-vars
    NUMBER = 3,
    // eslint-disable-next-line no-unused-vars
    DATE = 4,
    // eslint-disable-next-line no-unused-vars
    BOOLEAN = 5,
    // eslint-disable-next-line no-unused-vars
    SELECT = 6,
    // eslint-disable-next-line no-unused-vars
    ENUM = 7,
    // eslint-disable-next-line no-unused-vars
    PERCENT = 8,
}

// 공통으로 사용되는 인터페이스 정의
export interface BaseColumn {
    id: string;
    colspan?: number;
    rowspan?: number;
    type: ValueType;
    width?: number;
    format?: string;
    align?: 'left' | 'center' | 'right';
    parent?: string;
    ratio?: number;
    color?: string;
    textColor?: string;
    label?: string | number;
    readOnly?: boolean;
    resizable?: boolean;
    children?: BaseColumn[];
    cellBackgroundColor?: string;
    visible?: boolean;
    icon?: string;
}

// 요약 컬럼 인터페이스
export interface SummaryColumn extends BaseColumn {
    children?: SummaryColumn[];
    value?: any;
    width?: number;
    onClick?: () => void;
}

// 일반 컬럼 인터페이스 
export interface Column extends BaseColumn {
    width: number;
    filter?: Filter;
    options?: Array<{
        value: string | number;
        label: string;
        color?: string;
    }>;
    maxLength?: number;
    noMatchLabel?: string;
    isDropdownOpen?: boolean;
    children?: Column[];
    icon?: string;
    iconClick?: (rowId: string, tableId: string) => void;
    // 포커싱 가능 여부를 결정할 참조 컬럼 ID (해당 컬럼에 값이 있어야만 포커싱 가능)
    dependsOnColumn?: string;
    // 공휴일/주말 표시 여부
    holiday?: boolean;
    // 날짜 조합을 위한 참조 컬럼들 (year, month, day 등이 분리된 경우)
    holidayDateColumns?: {
        year?: string;
        month?: string;
        day?: string;
    };
}

// 필터 관련 타입 정의
export enum FilterOperator {
    // eslint-disable-next-line no-unused-vars
    EQUALS = 'equals',
    // eslint-disable-next-line no-unused-vars
    CONTAINS = 'contains',
    // eslint-disable-next-line no-unused-vars
    STARTS_WITH = 'startsWith',
    // eslint-disable-next-line no-unused-vars
    ENDS_WITH = 'endsWith',
    // eslint-disable-next-line no-unused-vars
    GREATER_THAN = 'greaterThan',
    // eslint-disable-next-line no-unused-vars
    LESS_THAN = 'lessThan'
}
type OrderType = 'asc' | 'desc' | '';

export interface Filter {
    type: ValueType;
    value?: string | number | Date | Array<string | number>;
    operator?: FilterOperator;
    options?: Array<string | number>;
    placeholder?: string;
    orderType?: OrderType;
}

// 셀 관련 인터페이스
export interface Cell {
    originalValue?: any;
    value?: any;
    color?: string;
    backgroundColor?: string;
    format?: string;
    canSelect?: boolean;
    selected?: boolean;
    readOnly?: boolean;
    align?: 'left' | 'center' | 'right';
    isDropdownOpen?: boolean;
}

// 행 관련 인터페이스
export interface RowData {
    data: { [columnId: string]: Cell };
}

export interface Row extends RowData {
    id: string;
    selected?: boolean;
    readOnly?: boolean;
}

// 위치 정보 인터페이스
interface Position {
    left: number;
    top: number;
    width: number;
    height: number;
}

// 편집 관련 인터페이스
export interface EditingCell {
    rowIndex: number;
    columnIndex: number;
    value: string;
    originalValue: string;
    position: Position;
    isCancel?: boolean;
    isDropdownOpen?: boolean;
    rowId: string;
    columnId: string;
}

export interface CellEditorProps {
    position: Position;
    value: string;
    editingCell: EditingCell;
    readOnly?: boolean;
    maxLength?: number;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

// 선택 관련 인터페이스
export interface SelectedCell {
    rowIndex: number;
    columnIndex: number;
}

export interface SelectedColumn {
    id: string;
    grabbed: boolean;
}



// 테이블 상태 인터페이스
export interface TableState {
    tableId: string;
    columns: { [columnId: string]: Column };
    summaryColumns: { [summaryColumnId: string]: SummaryColumn };
    hasSummaryColumn: boolean;
    selectedCell: SelectedCell;
    selectedColumn: SelectedColumn;
    canvasWidth: number;
    canvasHeight: number;
    contentWidth: number;
    contentHeight: number;
    scrollX: number;
    scrollY: number;
    maxScrollX: number;
    maxScrollY: number;
    hasVerticalScrollbar: boolean;
    hasHorizontalScrollbar: boolean;
    editingCell: EditingCell | null;
    isEditing: boolean;
    autoAddRow: boolean;
    drawEmptyRows?: boolean;
    isInitialized: boolean;
    fullRowSelect: boolean;
    rowFilter?: { [key: string]: (row: Row) => Row }
    onRowClick?: (row: Row) => void;
    onRowDoubleClick?: (rowId: string) => void;
    dropdownMenu: {
        isOpen: boolean;
        position: {
            x: number;
            y: number;
            width: number;
        };
        rowId: string;
        columnId: string;
    } | null;
    hoveredCell: {
        rowIndex: number;
        columnIndex: number;
    } | null;
    isSearchModalOpen: boolean;
    autoFit: boolean;
    autoFitPending: boolean;
}

export interface RowsState {
    [tableId: string]: EntityState<Row, string>;
}

export interface CellDimensions {
    x: number;
    y: number;
    width: number;
    height: number;
}